import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import oIconLibrary from 'utils/iconLibrary';
import IconOnlyButton from 'components/Buttons/IconOnlyButton';
import Modal from 'components/Layout/Modal/Modal';
import useTrapFocusInsideModal from 'utils/hooks/useTrapFocusInsideModal';
import useBodyScrollLock from 'utils/hooks/useBodyScrollLock';
import MobileSearchOverlay from './elements/MobileSearchOverlay';
import AdvancedSearch from './elements/AdvancedSearch';
import { QuickSearchSelect } from './elements/QuickSearch';
import AdvancedSearchDeepLinkHandler from './elements/AdvancedSearch/AdvancedSearchDeepLinkHandler';
import { fnComputeSearchOrigin } from './helpers';

// Captures and clears the origin ref as advanced search opens/closes, so
// the next open always recaptures from the current URL.
const useOriginLocationRef = (bOpen, oLocation) => {
  const oRef = useRef(null);
  useEffect(() => {
    if (!bOpen) {
      oRef.current = null;
    } else if (!oRef.current) {
      oRef.current = fnComputeSearchOrigin(oLocation);
    }
  }, [bOpen, oLocation]);
  return oRef;
};

/**
 * Toggles between QuickSearch and AdvancedSearch.
 * QuickSearch renders inline in the nav bar; AdvancedSearch opens in a
 * full-screen inline overlay on mobile and a modal on larger screens.
 */
const DirectorySearch = ({
  isMobileExpanded,
  bIsMobile,
  fnCloseMobileSearch,
}) => {
  const [bIsAdvancedSearchVisible, fnSetIsAdvancedSearchVisible] =
    useState(false);
  const [advSearchCriteria, setAdvSearchCriteria] = useState(undefined);
  const [bForceSubmit, fSetForceSubmit] = useState(false);
  const [bShowResultsInModal, setShowResultsInModal] = useState(false);
  const fnGoBackRef = useRef(null);
  const mobileOverlayRef = useRef(null);
  const sMobileTitleId = useId();
  const oLocation = useLocation();
  const fnNavigate = useNavigate();
  const originLocationRef = useOriginLocationRef(
    bIsAdvancedSearchVisible,
    oLocation
  );
  const bIsAdvSearchDeepLink =
    oLocation.pathname.includes('/search') && !!oLocation.search;

  const handleSubmitAdvancedSearch = useCallback((data) => {
    fSetForceSubmit(true);
    setAdvSearchCriteria(data);
    fnSetIsAdvancedSearchVisible(true);
  }, []);

  const handleBack = () => {
    if (fnGoBackRef.current) fnGoBackRef.current(false);
  };

  const bMobileOverlayOpen = bIsAdvancedSearchVisible && bIsMobile;

  const closeAdvancedSearch = useCallback(() => {
    fnSetIsAdvancedSearchVisible(false);
    // On mobile, collapse the navbar's mobile-search row too and restore
    // focus to its trigger. On desktop there's no row to close, and the
    // search toggle is display:none — calling the close handler there
    // would focus a hidden button and strand focus on <body>.
    if (bIsMobile) fnCloseMobileSearch?.();
    // Without these, AdvancedSearch's bForceSubmit effect will re-run the
    // previous query the next time the overlay opens.
    fSetForceSubmit(false);
    setAdvSearchCriteria(undefined);
    setShowResultsInModal(false);
    if (bIsAdvSearchDeepLink) {
      fnNavigate(originLocationRef.current ?? '/main');
    }
  }, [
    bIsMobile,
    bIsAdvSearchDeepLink,
    fnCloseMobileSearch,
    fnNavigate,
    originLocationRef,
  ]);

  useTrapFocusInsideModal(mobileOverlayRef, {
    bEnabled: bMobileOverlayOpen,
    bQueryDynamically: true,
    fnOnEscape: closeAdvancedSearch,
  });

  useBodyScrollLock(bMobileOverlayOpen);

  const advancedSearchContent = (
    <AdvancedSearch
      fnSetIsAdvancedSearchVisible={fnSetIsAdvancedSearchVisible}
      bForceSubmit={bForceSubmit}
      initialSearchCriteria={advSearchCriteria}
      onShowResultsChange={(showing, setShowResults) => {
        setShowResultsInModal(showing);
        fnGoBackRef.current = setShowResults;
      }}
    />
  );

  return (
    <>
      <div className='searchContainer'>
        <div className='search__magnifyIcon'>
          <FontAwesomeIcon icon={oIconLibrary.fontAwesome.search} />
        </div>
        <QuickSearchSelect
          handleSubmitAdvancedSearch={handleSubmitAdvancedSearch}
          bAutoFocus={isMobileExpanded}
        />
        <IconOnlyButton
          className='search__advSearchToggle'
          fnHandleClick={() => fnSetIsAdvancedSearchVisible(true)}
          sIcon={isMobileExpanded ? 'sliders' : 'expand'}
          sAriaLabel='Open advanced search'
          sFlavor='tertiary'
          sCypressId='toggle-advanced-search'
        />
      </div>
      {bIsAdvSearchDeepLink && (
        <AdvancedSearchDeepLinkHandler
          handleSubmitAdvancedSearch={handleSubmitAdvancedSearch}
        />
      )}
      {bIsAdvancedSearchVisible &&
        (bIsMobile ? (
          <MobileSearchOverlay
            oOverlayRef={mobileOverlayRef}
            sTitleId={sMobileTitleId}
            fnOnClose={closeAdvancedSearch}
            bShowBack={bShowResultsInModal}
            fnOnBack={handleBack}
            oChildren={advancedSearchContent}
          />
        ) : (
          <Modal
            fnSetShowModal={closeAdvancedSearch}
            sFlavor='standard'
            sModalTitle='Advanced Search'
            fnOnBack={bShowResultsInModal ? handleBack : null}
          >
            {advancedSearchContent}
          </Modal>
        ))}
    </>
  );
};
DirectorySearch.propTypes = {
  isMobileExpanded: PropTypes.bool,
  bIsMobile: PropTypes.bool,
  fnCloseMobileSearch: PropTypes.func,
};

DirectorySearch.defaultProps = {
  isMobileExpanded: false,
  bIsMobile: false,
  fnCloseMobileSearch: null,
};

export default DirectorySearch;
